import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, auth } from '../services/supabase';
import { invokeEdgeFunction } from '../services/edgeFunctions';
import { errorMessage, isRecord } from '../services/errors';
import { iconButton, tapTarget } from '../components/styles';
import CaldavSection from '../features/settings/CaldavSection';
import MailSection from '../features/settings/MailSection';
import OutlookSection from '../features/settings/OutlookSection';

interface EmailAddress {
  id: string;
  address: string;
  is_active: boolean;
  emails_received: number;
  created_at: string;
}

interface IntegrationConnection {
  id: string;
  provider: string;
  workspace_name: string;
  is_active: boolean;
  created_at: string;
}

export default function IntegrationsPage() {
  const queryClient = useQueryClient();

  // Email addresses
  const { data: emailAddresses } = useQuery({
    queryKey: ['email-addresses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_addresses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EmailAddress[];
    },
  });

  // Integration connections
  const { data: connections } = useQuery({
    queryKey: ['integration-connections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_connections')
        // Only these columns are readable; the provider token stays server-side.
        .select('id, provider, workspace_name, is_active, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as IntegrationConnection[];
    },
  });

  // Generate email address
  const generateEmail = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate unique email address
      const suffix = Math.random().toString(36).substring(2, 10);
      const address = `capture-${suffix}@inbound.cerebrodiem.com`;

      const { data, error } = await supabase
        .from('email_addresses')
        .insert({
          user_id: user.id,
          address,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-addresses'] });
    },
  });

  // Toggle email address
  const toggleEmail = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('email_addresses')
        .update({ is_active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-addresses'] });
    },
  });

  // Delete email address
  const deleteEmail = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('email_addresses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-addresses'] });
    },
  });

  // Slack OAuth: the server issues a single-use state for this user and the
  // Slack address to go to; the callback page finishes with that state.
  const connectSlack = useMutation({
    mutationFn: async () => {
      const result = await invokeEdgeFunction('slack-integration', {
        action: 'oauth_start',
        redirect_uri: `${window.location.origin}/integrations/slack/callback`,
      });
      const address = isRecord(result) ? result.authorize_url : undefined;
      if (typeof address !== 'string' || !address.startsWith('https://slack.com/')) {
        throw new Error('slack-integration did not say where to connect');
      }
      window.location.assign(address);
    },
  });

  // Disconnect integration
  const disconnectIntegration = useMutation({
    mutationFn: async (provider: string) => {
      const { error } = await supabase
        .from('integration_connections')
        .update({ is_active: false })
        .eq('provider', provider);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-connections'] });
    },
  });

  const slackConnection = connections?.find(c => c.provider === 'slack' && c.is_active);
  const teamsConnection = connections?.find(c => c.provider === 'teams' && c.is_active);

  return (
    // No padding of its own: the Layout pads every page.
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-on-surface mb-6">Integrations</h1>

      {/* Email Capture */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
          <span className="text-xl">📧</span>
          Email Capture
        </h2>
        <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6">
          <p className="text-on-surface-variant mb-4">
            Forward emails to your personal capture address to automatically import them as thoughts.
          </p>

          {emailAddresses?.length === 0 ? (
            <button
              onClick={() => generateEmail.mutate()}
              disabled={generateEmail.isPending}
              className={`${tapTarget} px-4 py-2 bg-primary text-on-primary rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50`}
            >
              {generateEmail.isPending ? 'Generating...' : 'Generate Email Address'}
            </button>
          ) : (
            <div className="space-y-3">
              {emailAddresses?.map(email => (
                // On a phone the address takes the width and its buttons go under it.
                <div key={email.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-surface-variant/30 rounded-lg p-3 sm:p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {/* The address has no spaces, so it may break at any character. */}
                      <code className="min-w-0 break-all text-sm bg-surface-variant px-2 py-1 rounded font-mono">
                        {email.address}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(email.address)}
                        className={`${iconButton} shrink-0 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-surface-variant`}
                        title="Copy"
                        aria-label="Copy the address"
                      >
                        📋
                      </button>
                    </div>
                    <p className="text-sm text-on-surface-variant mt-1">
                      {email.emails_received} emails received • {email.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleEmail.mutate({ id: email.id, is_active: !email.is_active })}
                      className={`${tapTarget} px-3 py-1 rounded-lg text-sm font-medium ${
                        email.is_active
                          ? 'bg-warning-container text-on-warning-container hover:bg-warning-container/80'
                          : 'bg-success-container text-on-success-container hover:bg-success-container/80'
                      }`}
                    >
                      {email.is_active ? 'Pause' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this email address?')) {
                          deleteEmail.mutate(email.id);
                        }
                      }}
                      className={`${tapTarget} px-3 py-1 bg-error-container text-on-error-container rounded-lg text-sm font-medium hover:bg-error-container/80`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-4 bg-secondary-container rounded-lg">
            <h4 className="font-medium text-on-secondary-container mb-2">How it works:</h4>
            <ol className="text-sm text-on-secondary-container space-y-1 list-decimal list-inside">
              <li>Forward any email to your capture address</li>
              <li>The email subject and body will be extracted</li>
              <li>AI will classify it and add to your knowledge base</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Slack Integration */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
          <span className="text-xl">💬</span>
          Slack
        </h2>
        <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6">
          {slackConnection ? (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="w-10 h-10 shrink-0 bg-primary-container rounded-lg flex items-center justify-center">
                    <span className="text-xl">🔗</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-on-surface">Connected to {slackConnection.workspace_name}</p>
                    <p className="text-sm text-on-surface-variant">
                      Since {new Date(slackConnection.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => disconnectIntegration.mutate('slack')}
                  className={`${tapTarget} px-4 py-2 bg-error-container text-on-error-container rounded-lg font-medium hover:bg-error-container/80`}
                >
                  Disconnect
                </button>
              </div>
              <div className="p-4 bg-success-container rounded-lg">
                <p className="text-on-success-container">
                  <span className="font-medium">✓ Active:</span> DM the Cerebro Diem bot or mention it in any channel to capture thoughts.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-on-surface-variant mb-4">
                Connect Slack to capture thoughts by messaging the Cerebro Diem bot or mentioning it in channels.
              </p>
              {connectSlack.isError && (
                <p role="alert" className="text-sm text-error mb-3">
                  Could not start connecting Slack: {errorMessage(connectSlack.error)}
                </p>
              )}
              <button
                onClick={() => connectSlack.mutate()}
                disabled={connectSlack.isPending}
                className={`${tapTarget} px-4 py-2 bg-slack text-on-slack rounded-lg font-medium hover:bg-slack-hover disabled:opacity-50 flex items-center gap-2`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                </svg>
                Connect Slack
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Microsoft Teams */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
          <span className="text-xl">👥</span>
          Microsoft Teams
        </h2>
        <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6">
          {teamsConnection ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="w-10 h-10 shrink-0 bg-secondary-container rounded-lg flex items-center justify-center">
                  <span className="text-xl">🔗</span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-on-surface">Connected to {teamsConnection.workspace_name}</p>
                  <p className="text-sm text-on-surface-variant">
                    Since {new Date(teamsConnection.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => disconnectIntegration.mutate('teams')}
                className={`${tapTarget} px-4 py-2 bg-error-container text-on-error-container rounded-lg font-medium hover:bg-error-container/80`}
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div>
              <p className="text-on-surface-variant mb-4">
                Connect Microsoft Teams to capture thoughts from Teams messages.
              </p>
              <button
                disabled
                className={`${tapTarget} px-4 py-2 bg-surface-variant text-on-surface-variant rounded-lg font-medium cursor-not-allowed flex items-center gap-2`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.25 3H14.5v3.5h5.5v12H14.5V22h4.75a2.25 2.25 0 0 0 2.25-2.25V5.25A2.25 2.25 0 0 0 19.25 3zM17 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM13 6.5H4.75A2.25 2.25 0 0 0 2.5 8.75v10.5A2.25 2.25 0 0 0 4.75 21.5H13V6.5zM6.5 18.5v-9h4.5v9H6.5z"/>
                </svg>
                Coming Soon
              </button>
            </div>
          )}
        </div>
      </section>

      <OutlookSection />
      <CaldavSection />
      <MailSection />

      {/* Webhooks */}
      <section>
        <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
          <span className="text-xl">🔗</span>
          Webhooks & API
        </h2>
        <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6">
          <p className="text-on-surface-variant mb-4">
            Use webhooks to integrate Cerebro Diem with your own tools and automation.
          </p>
          <div className="p-4 bg-surface-variant/30 rounded-lg">
            <p className="text-sm text-on-surface-variant">
              Coming soon: REST API and webhook endpoints for custom integrations.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
